
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Key, Shield, Check, X } from 'lucide-react';
import { usePassword } from '@/contexts';
import { Progress } from "@/components/ui/progress";

const CreatePasswordForm = () => {
  const { createPassword, passwordStrength } = usePassword();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [strength, setStrength] = useState({ isValid: false, message: '' });
  const [passwordFocused, setPasswordFocused] = useState(false);

  useEffect(() => {
    if (newPassword) {
      setStrength(passwordStrength(newPassword));
    } else {
      setStrength({ isValid: false, message: '' });
    }
  }, [newPassword, passwordStrength]);

  const handleCreatePassword = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!strength.isValid) {
      setError(strength.message);
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    
    createPassword(newPassword);
    setNewPassword('');
    setConfirmPassword('');
  };

  // Calculate password strength score (0-100)
  const calculateStrengthScore = (password: string): number => {
    if (!password) return 0;
    
    let score = 0;
    if (password.length >= 8) score += 25;
    if (/[A-Z]/.test(password)) score += 25;
    if (/[a-z]/.test(password)) score += 25;
    if (/[0-9]/.test(password)) score += 15;
    if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) score += 10;
    
    return Math.min(score, 100);
  };

  const strengthScore = calculateStrengthScore(newPassword);
  
  const getStrengthColor = (score: number): string => {
    if (score < 40) return "bg-red-500";
    if (score < 70) return "bg-yellow-500";
    return "bg-green-500";
  };

  return (
    <form onSubmit={handleCreatePassword} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="new-password">New Password</Label>
        <Input
          id="new-password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          onFocus={() => setPasswordFocused(true)}
          onBlur={() => setPasswordFocused(false)}
          required
          placeholder="Enter a new password"
        />
        
        {newPassword && (
          <>
            <Progress 
              value={strengthScore} 
              className={`h-1 w-full ${getStrengthColor(strengthScore)}`} 
            />
            <div className="text-xs text-muted-foreground">
              {strength.isValid ? "Password strength: Strong" : "Password strength: Weak"}
            </div>
          </>
        )}
        
        {passwordFocused && (
          <div className="text-xs space-y-1 mt-2 p-2 border rounded-md bg-background">
            <h4 className="font-medium text-sm">Password Requirements:</h4>
            <div className="grid gap-1">
              <RequirementCheck 
                met={newPassword.length >= 8}
                label="At least 8 characters"
              />
              <RequirementCheck 
                met={/[A-Z]/.test(newPassword)}
                label="One uppercase letter"
              />
              <RequirementCheck 
                met={/[a-z]/.test(newPassword)}
                label="One lowercase letter"
              />
              <RequirementCheck 
                met={/[0-9]/.test(newPassword)}
                label="One number"
              />
              <RequirementCheck 
                met={/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword)}
                label="One special character"
              />
            </div>
          </div>
        )}
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="confirm-password">Confirm Password</Label>
        <Input
          id="confirm-password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          placeholder="Confirm your password"
        />
        {newPassword && confirmPassword && newPassword !== confirmPassword && (
          <div className="flex items-center gap-1 text-xs text-red-500 mt-1">
            <X className="h-3 w-3" />
            <span>Passwords don't match</span>
          </div>
        )}
      </div>
      
      {error && (
        <div className="text-sm text-red-500 flex items-center gap-1 mt-1">
          <Shield className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}
      
      <div className="flex justify-end pt-2">
        <Button 
          type="submit" 
          className="bg-nalc-teal hover:bg-nalc-teal/90"
          disabled={!strength.isValid || newPassword !== confirmPassword}
        >
          <Key className="mr-2 h-4 w-4" />
          Create Password
        </Button>
      </div>
    </form>
  );
};

// Helper component to display password requirement checks
const RequirementCheck = ({ met, label }: { met: boolean; label: string }) => {
  return (
    <div className={`flex items-center gap-1 text-xs ${met ? 'text-green-500' : 'text-muted-foreground'}`}>
      {met ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
      <span>{label}</span>
    </div>
  );
};

export default CreatePasswordForm;
